import * as React from "react";
import { cn } from "@/lib/utils";

export interface PairingFormSkeletonProps
  extends React.HTMLAttributes<HTMLDivElement> {}

/**
 * Loading skeleton for PairingItemForm component
 *
 * Matches PairingItemForm layout with animated pulse effect.
 * Used to show loading state while fetching pairing form data.
 *
 * Layout:
 * - Title section
 * - Product image (square aspect ratio)
 * - Product info (brand + title)
 * - NFC ID badge
 * - Two code input fields
 * - Validation button
 *
 * @example
 * ```tsx
 * <PairingFormSkeleton />
 * ```
 */
export const PairingFormSkeleton = React.forwardRef<
  HTMLDivElement,
  PairingFormSkeletonProps
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      role="status"
      aria-live="polite"
      aria-label="Loading pairing form"
      className={cn(
        "flex flex-col gap-8 items-center animate-pulse",
        className
      )}
      {...props}
    >
      {/* Title Section */}
      <div className="flex gap-4 items-center w-full">
        <div className="h-6 bg-gray-100 rounded w-full" />
      </div>

      {/* Product Image */}
      <div className="relative h-[12.6875rem] w-[11.9375rem] shrink-0">
        <div className="absolute inset-0 bg-gray-100 rounded-[0.3125rem]" />
      </div>

      {/* NFC ID Badge */}
      <div className="bg-gray-100 h-12 w-full rounded-[0.3125rem]" />

      {/* Secret Code Section */}
      <div className="flex flex-col gap-4 items-start w-full">
        {/* Label */}
        <div className="flex items-center justify-between w-full">
          <div className="h-5 bg-gray-100 rounded w-48" />
          <div className="size-[0.9375rem] bg-gray-100 rounded" />
        </div>

        {/* Two Code Input Fields */}
        <div className="flex gap-4 items-start w-full">
          <div className="flex-1 h-12 bg-gray-100 rounded" />
          <div className="flex-1 h-12 bg-gray-100 rounded" />
        </div>
      </div>

      {/* Validation Button */}
      <div className="w-full h-10 bg-gray-100 rounded-[0.375rem]" />
    </div>
  );
});

PairingFormSkeleton.displayName = "PairingFormSkeleton";
